import { useEffect, useRef } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Style the confirm button as destructive (e.g. for "Restart"). */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Lightweight confirmation dialog for in-widget actions (e.g. "Restart
 * conversation"). Zero-dep (no Radix) to keep the iframe bundle small.
 * Overlays the chat panel, traps Esc, and autofocuses the confirm button.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      data-testid="confirm-dialog"
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label={cancelLabel}
        onClick={onCancel}
        className="absolute inset-0 cursor-default bg-black/40 animate-in fade-in duration-150 dark:bg-black/60"
      />
      {/* Card */}
      <div className="relative z-10 w-full max-w-[320px] rounded-2xl border border-border bg-card p-5 shadow-lg animate-in fade-in zoom-in-95 duration-150 dark:border-white/[0.1] dark:bg-[#1e2836]">
        <p id="confirm-title" className="text-base font-semibold text-foreground dark:text-white">
          {title}
        </p>
        {description && (
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground dark:text-white/60">
            {description}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="confirm-cancel"
            className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground dark:text-white/70 dark:hover:bg-white/[0.08]"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            data-testid="confirm-accept"
            className={
              'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
              (destructive
                ? 'bg-destructive text-white hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90')
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
